import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { showSuccess, showError } from '../utils/notifications';
import API_CONFIG from '../config';
import { translateErrorMessage } from '../utils/errorTranslations';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [validToken, setValidToken] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      setValidToken(true);
    } else {
      setValidToken(false);
      showError('Token de réinitialisation manquant ou invalide');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      showError('Token de réinitialisation manquant');
      return;
    }
    
    if (password !== confirmPassword) {
      showError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      showError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: password,
          confirm_password: confirmPassword
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showSuccess('Mot de passe réinitialisé avec succès ! Redirection vers la connexion...');
        
        // Redirection vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Gérer les erreurs
        let errorMessage = 'Erreur lors de la réinitialisation';
        
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const firstError = data.detail[0];
            if (firstError && firstError.msg) {
              errorMessage = firstError.msg;
            }
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        
        const translatedError = translateErrorMessage(errorMessage);
        showError(translatedError);
      }
    } catch (error) {
      showError('Erreur réseau, veuillez réessayer');
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (validToken === false) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/10 to-transparent"></div>
        
        <div className="max-w-md w-full space-y-3 z-10" style={{transform: 'translateY(-25%)'}}>
          <div className="flex flex-col items-center">
            <img 
              src="/images/logo-full.png" 
              alt="Mes Prix" 
              className="h-48 sm:h-48 lg:h-48 w-auto mx-auto" 
            />
            <h2 className="mt-1 text-center text-lg sm:text-xl font-extrabold text-gray-900">
              Lien invalide
            </h2>
            <p className="mt-1 text-center text-sm text-gray-600">
              Le lien de réinitialisation est invalide ou a expiré
            </p>
          </div>
          
          <div className="text-center">
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-700">
                Ce lien de réinitialisation n'est plus valide.
              </p>
              <p className="text-sm text-red-700 mt-2">
                Veuillez demander un nouveau lien de réinitialisation.
              </p>
            </div>
          </div>
          
          <div className="space-y-3">
            <Link 
              to="/forgot-password" 
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Demander un nouveau lien
            </Link>
            <Link 
              to="/login" 
              className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Retour à la connexion
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-gray-100/80 to-transparent p-6">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-2">© 2025 Mes Prix - Tous droits réservés</p>
            <div className="flex justify-center space-x-4 text-xs text-gray-400">
              <span>Conditions d'utilisation</span>
              <span>•</span>
              <span>Politique de confidentialité</span>
              <span>•</span>
              <span>Support</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (validToken === null) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status">
            <span className="sr-only">Chargement...</span>
          </div>
          <p className="mt-2 text-gray-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4">
      {/* Élément décoratif en haut */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/10 to-transparent"></div>
      
      <div className="max-w-md w-full space-y-3 z-10" style={{transform: 'translateY(-25%)'}}>
        <div className="flex flex-col items-center">
          {/* Logo pour tous les écrans */}
          <img 
            src="/images/logo-full.png" 
            alt="Mes Prix" 
            className="h-48 sm:h-48 lg:h-48 w-auto mx-auto" 
          />
          <h2 className="mt-1 text-center text-lg sm:text-xl font-extrabold text-gray-900">
            Nouveau mot de passe
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            Choisissez votre nouveau mot de passe
          </p>
        </div>
        
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="password" className="sr-only">Nouveau mot de passe</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Nouveau mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">Confirmer le mot de passe</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              disabled={loading}
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </button>
          </div>
        </form>
        
        <div className="text-sm text-center">
          <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
            Retour à la connexion
          </Link>
        </div>
      </div>
      
      {/* Footer en bas */}
      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-gray-100/80 to-transparent p-6">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">© 2025 Mes Prix - Tous droits réservés</p>
          <div className="flex justify-center space-x-4 text-xs text-gray-400">
            <span>Conditions d'utilisation</span>
            <span>•</span>
            <span>Politique de confidentialité</span>
            <span>•</span>
            <span>Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 